import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { withTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { Box, Button, Grid, IconButton, useMediaQuery } from "@mui/material";
import ViewIcon from "@mui/icons-material/RemoveRedEye";
import PropTypes from "prop-types";

import BreadCrumb from "Components/Common/BreadCrumb";
import Backdrop from "Components/Common/Backdrop";
import SelectField from "Components/Common/SelectField";
import BaseTable from "Components/Common/BaseTable";
import Tooltip from "Components/Common/Tooltip";
import InputFieldText from "Components/Common/InputFieldText";
import DocumentPreviewModal from "Components/Common/DocumentPreviewModal";
import {
  POWER_BACKUP_POWER_BILLING_FINANCE,
  POWER_CONSUMER_BILLING_INCHARGE,
  POWER_CONSUMER_BILLING_OPERATOR,
  powerAdmin2Roles,
  SUPER_ADMIN,
} from "common/constant";
import { getOptionLabel, getOptionValue } from "common/enums";
import BackupPowerBillReviewModal from "./BackupPowerBillReviewModal";

import { useGetBillingCycleOptionsQuery } from "apis/BillingCycle";
import {
  useLazyGetBackupPowerQuery,
  usePostUploadBackupPowerBillsMutation,
} from "apis/BackupPower";

import { convertUTCDateToLocalDate } from "utils/convertUTCDateToLocalDate";
import { getImageUrl, isAuthorizedUser } from "utils";
import getFileExtension from "utils/getFileExtension";
import { toast } from "react-toastify";

const BillingBackupPower = ({ t }) => {
  const [uploadBackupPowerBills, { isLoading: isBackupPowerBillsUploading }] =
    usePostUploadBackupPowerBillsMutation();

  const [billCycleId, setBillCycleId] = useState("");
  const [documentToPreview, setDocumentToPreview] = useState(null);
  const [consumerNumber, setConsumerNumber] = useState("");
  const [isDispatchBtnClicked, setIsDispatchBtnClicked] = useState(false);
  const [isResetPageRegistered, setIsResetPageRegistered] = useState(false);

  const { roles } = useSelector((state) => state.Login);
  const resetPageRef = useRef();

  const [searchParams, setSearchParams] = useSearchParams();
  const billingCycleIdForBackupPower = searchParams.get("billingCycleId");
  const [billCycleIdForDownloadBtn, setBillCycleIdForDownloadBtn] =
    useState("");

  const md = useMediaQuery("(min-width: 600px) and (max-width: 899px)");
  const sm = useMediaQuery("(max-width: 599px)");

  const [
    getBackupPowerData,
    { data: { data: backupPowerData } = {}, isLoading },
  ] = useLazyGetBackupPowerQuery();

  const {
    data: { data: billingCycleOptions } = {},
    isLoading: isBillingCycleOptionsLoading,
  } = useGetBillingCycleOptionsQuery();

  const handleResetPage = useCallback(resetPageRef.current, [
    isResetPageRegistered,
  ]);

  const searchBtnHandler = async (e) => {
    e.preventDefault();
    await getBackupPowerData(billCycleId, { consumerNumber });
    handleResetPage();
  };

  const clearBtnHandler = async () => {
    await getBackupPowerData(billCycleId, {
      consumerNumber: "",
    });
    setConsumerNumber("");
    handleResetPage();
  };

  const updatedBackupPowerOptions = useMemo(
    () =>
      billingCycleOptions?.length
        ? billingCycleOptions
            .filter(
              ({ subscriptionType }) =>
                subscriptionType ===
                getOptionValue("subscriptionTypes", "Backup Power")
            )
            .map(({ billDateDuration, id, status }) => ({
              label: billDateDuration,
              value: id,
              status,
            }))
        : [],
    [billingCycleOptions]
  );

  const columns = useMemo(
    () => [
      {
        title: "Consumer Number",
        field: "consumerNumber",
      },
      {
        title: "Consumer Name",
        field: "consumerName",
      },
      {
        title: "Bill Number",
        field: "billNumber",
      },
      {
        title: "Payment Date",
        renderCell: ({ paymentDueDate }) =>
          paymentDueDate
            ? convertUTCDateToLocalDate(paymentDueDate).format("DD-MM-YYYY")
            : "--",
      },
      {
        title: "Net Payable",
        renderCell: ({ netPayable }) => (
          <span className="text-nowrap"> &#8377; {netPayable}</span>
        ),
      },
      {
        title: "Current Month Bill",
        renderCell: ({ currentMonthBill }) => (
          <span className="text-nowrap"> &#8377; {currentMonthBill}</span>
        ),
      },
      {
        title: "Pending Dues",
        renderCell: ({ pendingDues }) => (
          <span className="text-nowrap"> &#8377; {pendingDues}</span>
        ),
      },

      {
        title: "Status",
        renderCell: ({ status }) =>
          getOptionLabel("billCalculationStatus", status),
      },
      {
        title: "Actions",
        renderCell: ({ url, billNumber }) => (
          <Box minWidth="100px">
            <IconButton
              onClick={() =>
                setDocumentToPreview({
                  url: getImageUrl(url),
                  documentTypeName: "Backup Power Bill",
                  documentType: "pdf",
                  documentName: `${billNumber}-backup-Power`,
                })
              }
              disabled={!url}
            >
              <Tooltip title={t("View Backup Power bill receipt")}>
                <ViewIcon
                  fontSize="small"
                  color={url ? "primary" : "disabled"}
                  className="cursor-pointer"
                />
              </Tooltip>
            </IconButton>
          </Box>
        ),
      },
    ],
    [backupPowerData]
  );

  const handleUploadFile = async (selectedFile) => {
    try {
      const formData = new FormData();
      const fileExtension = getFileExtension(selectedFile.name)?.toLowerCase();
      if (fileExtension !== "zip") {
        toast.error(
          "Invalid file format, Please upload file with .zip extension."
        );
      } else {
        formData.append("file", selectedFile);
        await uploadBackupPowerBills(formData);
      }
    } catch (err) {}
  };

  useEffect(() => {
    (async () => {
      try {
        if (updatedBackupPowerOptions?.length) {
          const id =
            updatedBackupPowerOptions.find(
              ({ status }) =>
                status !== getOptionValue("billingStatus", "Closed")
            )?.value ??
            updatedBackupPowerOptions.at(-1)?.value ??
            "";
          await getBackupPowerData(id);
          setBillCycleId(id);
        }
      } catch (error) {}
    })();
  }, [updatedBackupPowerOptions]);

  return (
    <>
      {!!documentToPreview && (
        <DocumentPreviewModal
          isOpen
          handleClose={() => setDocumentToPreview(null)}
          {...documentToPreview}
        />
      )}

      {(!!billingCycleIdForBackupPower || !!billCycleIdForDownloadBtn) && (
        <BackupPowerBillReviewModal
          handleClose={() => {
            searchParams.delete("billingCycleId");
            setSearchParams(searchParams);
            setIsDispatchBtnClicked(false);
            setBillCycleIdForDownloadBtn("");
          }}
          isDispatchModal={isDispatchBtnClicked}
          billCycleIdForDownloadBtn={billCycleIdForDownloadBtn}
        />
      )}

      <Backdrop
        open={
          isLoading ||
          isBillingCycleOptionsLoading ||
          isBackupPowerBillsUploading
        }
      />

      <BreadCrumb title={t("Backup Power")} pageTitle={t("Billing")} />
      <form onSubmit={searchBtnHandler}>
        <Grid container columnSpacing={2} className="mb-1">
          <Grid item lg={3} md={4} sm={6} xs={12}>
            <SelectField
              label={t("Select bill cycle")}
              name="billCycleId"
              value={
                updatedBackupPowerOptions?.find(
                  ({ value }) => value === billCycleId
                ) ?? ""
              }
              onChange={(_, { value }) => setBillCycleId(value)}
              options={updatedBackupPowerOptions || []}
            />
          </Grid>
          <Grid item lg={3} md={4} sm={6} xs={12}>
            <InputFieldText
              label={t("Consumer Number")}
              placeholder={t("Enter consumer number")}
              type="number"
              value={consumerNumber}
              onChange={(e) => setConsumerNumber(e.target.value)}
            />
          </Grid>
          <Grid item>
            <Button variant="outlined" className="mb-2 me-2" type="submit">
              {t("Search")}
            </Button>
            <Button
              variant="outlined"
              onClick={clearBtnHandler}
              className="mb-2 me-2"
            >
              {t("Clear")}
            </Button>
            {isAuthorizedUser(roles, [
              SUPER_ADMIN,
              POWER_CONSUMER_BILLING_OPERATOR,
              POWER_CONSUMER_BILLING_INCHARGE,
            ]) && (
              <Button
                variant="contained"
                component="label"
                className="mb-2"
                sx={{ fontSize: "13px" }}
              >
                {t("Upload")}
                <input
                  style={{
                    display: "none",
                  }}
                  accept=".zip"
                  type="file"
                  onChange={async (e) => {
                    await handleUploadFile(e.target.files[0]);
                    e.target.value = null;
                  }}
                />
              </Button>
            )}
          </Grid>
          <Grid item sx={{ pt: 0.1 }}>
            {!!backupPowerData?.length &&
              isAuthorizedUser(roles, [
                ...powerAdmin2Roles,
                POWER_CONSUMER_BILLING_OPERATOR,
                POWER_CONSUMER_BILLING_INCHARGE,
                POWER_BACKUP_POWER_BILLING_FINANCE,
                SUPER_ADMIN,
              ]) && (
                <>
                  {isAuthorizedUser(roles, POWER_CONSUMER_BILLING_INCHARGE) && (
                    <Button
                      onClick={() => {
                        searchParams.set("billingCycleId", billCycleId);
                        setSearchParams(searchParams);
                        setIsDispatchBtnClicked(true);
                      }}
                      variant="contained"
                      sx={{ fontSize: "13px" }}
                      className="mb-2 me-2"
                    >
                      {t("Dispatch")}
                    </Button>
                  )}
                  <Button
                    onClick={() => {
                      setBillCycleIdForDownloadBtn(billCycleId);
                    }}
                    sx={{ fontSize: "13px" }}
                    className="mb-2 me-2"
                    variant="contained"
                  >
                    {t("Download")}
                  </Button>
                  {!isAuthorizedUser(roles, SUPER_ADMIN) && (
                    <Button
                      onClick={() => {
                        searchParams.set("billingCycleId", billCycleId);
                        setSearchParams(searchParams);
                      }}
                      sx={{ fontSize: "13px" }}
                      className="mb-2 me-2"
                      variant="contained"
                    >
                      {t("Review Bills")}
                    </Button>
                  )}
                </>
              )}
          </Grid>
        </Grid>
      </form>

      <BaseTable
        data={backupPowerData}
        columns={columns}
        pageSize={10}
        registerResetPage={(cb) => {
          resetPageRef.current = cb;
          setIsResetPageRegistered((prev) => !prev);
        }}
        displaySrNo
        maxHeight={
          sm
            ? "calc(100vh - 350px)"
            : md
            ? "calc(100vh - 300px)"
            : "calc(100vh - 250px)"
        }
      />
    </>
  );
};

BillingBackupPower.propTypes = {
  t: PropTypes.func,
};

export default withTranslation()(BillingBackupPower);
